import React, { useEffect, useMemo, useState } from 'react';
import Select, { components } from 'react-select';
import { ReactComponent as ChevronDown } from '../svg/chevron-down.svg';
import { connect } from 'react-redux';
import {
    filtersCurrenciesSelector,
    newFiltersPeriodByTariffSelector,
    newFiltersTariffsSelector,
} from '../redux/selectors/filtersSelector';
import { pagesTariffsByProductSelector, sessionKey } from '../redux/selectors/pagesSelectors';
import {
    setAccount,
    setCurrency,
    setDomains,
    setNewPeriod,
    setNewTariff,
    setRenew,
} from '../redux/actions/cartAction';
import {
    calculateCartTotal,
    calculateProductTotal,
    cartCartSelector,
} from '../redux/selectors/cartSelector';
import usePricePerPeriod from '../hooks/usePricePerPeriod';
import {
    productAvailableAccountsSelector,
    productCurrentDomainsSelector,
} from '../redux/selectors/productSelector';
import { getLogger } from '../lib/devMode';
import getBillingUnitsCount from '../resolvers/getBillingUnitsCount';
import getTariffOptionsByGroup from '../resolvers/getTariffOptionsByGroup';
import getPeriodLabel from '../resolvers/getPeriodLabel';
import { MINIMUM_ALLOWED_ORDER_TOTAL, MINIMUM_ALLOWED_PRODUCT_PRICE } from '../constants/constants';
import { default as _u } from 'underscore';

function Filters({
    product,
    product_id,

    periodsList,
    domainsList,
    accountsList,
    tariffsByPeriod,
    currenciesList,
    cart,
    setCurrency,
    setTariff,
    setPeriod,
    setDomains,
    setAccount,
    sessionKey,
}) {
    const [domainsSelectorInitialized, setDomainsSelectorInitialized] = useState(false);
    const [accountSelectorInitialized, setAccountSelectorInitialized] = useState(false);

    const packageSelectStyles = {
        menu: ({ width, ...css }) => ({
            ...css,
            width: 'max-content',
            minWidth: '20%',
        }),
        option: (css) => ({ ...css, width: 'max-content', padding: '0 2px' }),
    };

    const DropdownIndicator = (props) => {
        return (
            <components.DropdownIndicator {...props}>
                <ChevronDown className={'icon'} />
            </components.DropdownIndicator>
        );
    };

    const pricePerPeriod = usePricePerPeriod();

    const domainsOptions = useMemo(() => {
        return domainsList.map((domainLine) => {
            return {
                value: domainLine?.cert_id,
                label: domainLine?.domain,
            };
        });
    }, [domainsList]);

    const accountsOptions = useMemo(() => {
        return accountsList.map((accountLine) => {
            return {
                value: accountLine?.account_id,
                label: `${accountLine?.org_name} (${accountLine?.status?.toLowerCase()})`,
            };
        });
    }, [accountsList]);

    const domainsOptionsSelected = () => {
        return cart?.domains?.map((domain) => {
            return {
                value: domain,
                label: domainsList.find((item) => item.cert_id === domain)?.domain,
            };
        });
    };

    const accountOptionSelected = () => {
        return {
            value: cart?.account,
            label: accountsList.find((item) => item.account_id === cart?.account)?.org_name,
        };
    };

    useEffect(() => {
        setDomainsSelectorInitialized(false);
        setAccountSelectorInitialized(false);
    }, [sessionKey]);

    useEffect(() => {
        if (!domainsSelectorInitialized && domainsList.length) {
            let defaultDomains = domainsOptionsSelected();
            if (
                !defaultDomains ||
                !defaultDomains.length ||
                !domainsList.find((item) => item?.cert_id === defaultDomains[0]?.value)
            ) {
                // There is at least one domain in the listing.
                const firstDomain = domainsList.find(Boolean);
                defaultDomains = [
                    {
                        value: firstDomain?.cert_id,
                        label: firstDomain?.domain,
                    },
                ];
            }
            setDomainsSelectorInitialized(true);
            setDomains({ domains: defaultDomains });
            getLogger().debug(
                'Setting up the default domains selection: ' + JSON.stringify(defaultDomains)
            );
        } else if (!sessionKey) {
            setDomains({ domains: [] });
            getLogger().debug('Unsetting domains since non registered user does not have them');
        }
    }, [domainsList, domainsSelectorInitialized]);

    useEffect(() => {
        if (accountsList.length && product?.product_id) {
            const defaultAccount = accountOptionSelected();
            setAccountSelectorInitialized(true);
            setAccount({ account: defaultAccount?.value, productId: product_id });
        } else if (!sessionKey) {
            setAccount({ account: undefined });
            getLogger().debug('Unsetting account ID since non registered user does not have it');
        }
    }, [accountsList, accountSelectorInitialized, product?.product_id]);

    const tariffsOptions = useMemo(() => {
        const nonMainProducts = Object.values(cart?.products)?.filter(
            (item) => item?.product_id !== product?.product_id
        );
        const tariffs = Object.values(tariffsByPeriod)
            ?.sort((a, b) => a.services - b.services)
            ?.map((tariff) => {
                const label = pricePerPeriod(
                    tariff,
                    // @see https://git.cleantalk.org/Website/pay-cleantalk-org/-/issues/3 for details
                    // If rendered option corresponds to a package with a different billing period than currently
                    // selected - do not calculate discountable price.
                    tariff.billing_period &&
                        tariff.billing_period === product?.period?.billing_period
                        ? product.period
                        : null
                );
                const totalCart = calculateCartTotal({
                    ...cart,
                    products: _u.object(
                        nonMainProducts?.map((item) => item?.product_id),
                        nonMainProducts
                    ),
                });
                const totalProduct = calculateProductTotal(
                    { ...product, tariff: tariff },
                    cart,
                    true,
                    tariffsByPeriod
                );
                const total = (totalCart?.total ?? 0) + totalProduct;
                return {
                    value: tariff?.tariff_id,
                    label: label,
                    name: tariff?.name,
                    services: getBillingUnitsCount({ tariff }),
                    isDisabled: tariff.current_tariff ? false : !!tariff?.isDisabled, //TODO ставим Disabled,
                    billing_period: tariff?.billing_period,
                    subscribable: !!tariff?.allow_subscribe,
                };
            });
        return getTariffOptionsByGroup(product_id, tariffs);
    }, [tariffsByPeriod, product?.period, product?.tariff]);

    return (
        <div className={'section__row'}>
            <ul className={'filter__list'}>
                {!!accountsList.length && (
                    <li className={'section__cell filter__item'}>
                        <label htmlFor="accountsList" className={'filter__label text-5'}>
                            Account:
                        </label>
                        <Select
                            id={`accountsList-${product?.product_id}`}
                            options={accountsOptions}
                            className={'filter__selector ml-2'}
                            classNamePrefix="filter"
                            isSearchable={true}
                            components={{ DropdownIndicator }}
                            onChange={(e) => {
                                setAccount({ account: e?.value, productId: product?.product_id });
                            }}
                            value={accountOptionSelected()}
                            isOptionDisabled={(option) => option?.isDisabled}
                        />
                    </li>
                )}
                {!!domainsList.length && (
                    <li className={'section__cell filter__item'}>
                        <label htmlFor="domainsList" className={'filter__label text-5'}>
                            Domains:
                        </label>
                        <Select
                            id={`domainsList-${product?.product_id}`}
                            options={domainsOptions}
                            className={'filter__selector ml-2'}
                            classNamePrefix="filter"
                            isSearchable={true}
                            components={{ DropdownIndicator }}
                            isMulti
                            onChange={(e) => {
                                setDomains({ domains: Array.isArray(e) ? e : [] });
                            }}
                            value={domainsOptionsSelected()}
                            isOptionDisabled={(option) => option?.isDisabled}
                        />
                    </li>
                )}
                {/* Period selector is moved before tariff selector according to the design */}
                {
                    <li className={'section__cell filter__item filter__item--period'}>
                        <label htmlFor="categoryList" className={'filter__label text-5'}>
                            Period
                        </label>
                        <Select
                            id={`periodList-${product?.product_id}`}
                            options={
                                periodsList
                                    ? Object.values(periodsList)?.map((period) => {
                                          return {
                                              value: period?.name,
                                              label: getPeriodLabel(
                                                  period?.name,
                                                  period,
                                                  cart?.currency?.sign
                                              ),
                                              isDisabled: period?.isDisabled,
                                          };
                                      })
                                    : null
                            }
                            className={'filter__selector ml-2'}
                            classNamePrefix="filter"
                            isSearchable={true}
                            components={{ DropdownIndicator }}
                            onChange={(e) => {
                                setPeriod(periodsList[e.value], product_id);
                            }}
                            isOptionDisabled={(option) => option?.isDisabled}
                            value={{
                                value: product?.period?.name,
                                label: getPeriodLabel(
                                    product?.period?.name,
                                    product?.period,
                                    cart?.currency?.sign
                                ),
                            }}
                            defaultValue={{ label: 'Select', value: 0 }}
                            //menuIsOpen={true}
                        />
                    </li>
                }
                {tariffsByPeriod && Object.values(tariffsByPeriod)?.length > 1 && (
                    <li
                        className={
                            'section__cell section__cell--package-list filter__item filter__item--package'
                        }
                    >
                        <label htmlFor="packageList" className={'filter__label text-5'}>
                            Package
                        </label>
                        <Select
                            id={`packageList-${product?.product_id}`}
                            styles={packageSelectStyles}
                            options={tariffsOptions}
                            className={`filter__selector ml-2 filter__selector--product-${
                                product?.product_id ?? 0
                            }`}
                            classNamePrefix="filter"
                            isSearchable={true}
                            components={{ DropdownIndicator }}
                            onChange={(e) => {
                                setTariff({
                                    tariff: tariffsByPeriod[e.value],
                                    product_id: product_id,
                                    change: true,
                                });
                            }}
                            isOptionDisabled={(option) => option?.isDisabled}
                            value={{
                                value: product?.tariff?.tariff_id,
                                label: product?.tariff
                                    ? pricePerPeriod(product?.tariff, product?.period)
                                    : product?.tariff?.name,
                            }}
                            //menuIsOpen={true}
                        />
                    </li>
                )}
                <li className={'section__cell filter__item filter__item--currency'}>
                    <label htmlFor="currencyList" className={'filter__label text-5'}>
                        Currency
                    </label>
                    <Select
                        id={'currencyList'}
                        options={Object.values(currenciesList)?.map((currency) => ({
                            value: currency.currency,
                            label: currency.currency,
                        }))}
                        className={'filter__selector ml-2'}
                        classNamePrefix="filter"
                        isSearchable={true}
                        components={{ DropdownIndicator }}
                        onChange={(e) => {
                            setCurrency(currenciesList[e?.value]);
                        }}
                        value={{ value: cart?.currency?.currency, label: cart?.currency?.currency }}
                        //menuIsOpen={true}
                    />
                </li>
            </ul>
        </div>
    );
}

const mapStateToProps = (state, props) => ({
    periodsList: newFiltersPeriodByTariffSelector(state, props),
    tariffListByProduct: pagesTariffsByProductSelector(state),
    currenciesList: filtersCurrenciesSelector(state),
    cart: cartCartSelector(state),
    tariffsByPeriod: newFiltersTariffsSelector(state, props),
    domainsList: productCurrentDomainsSelector(state),
    accountsList: productAvailableAccountsSelector(state),
    sessionKey: sessionKey(state),
});

const mapDispatchToProps = (dispatch) => ({
    setCurrency: (currency) => dispatch(setCurrency(currency)),
    setPeriod: (period, product_id) => dispatch(setNewPeriod({ period, product_id })),
    setTariff: (data) => dispatch(setNewTariff(data)),
    setDomains: (data) => dispatch(setDomains(data)),
    setAccount: (data) => dispatch(setAccount(data)),
    setCartRenew: (value) => dispatch(setRenew(value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Filters);
